/**
 * <bundle-protocole>
 *
 * Pilote l'UI du bloc « Bundle / protocole » : sélection d'un palier, choix du
 * mode d'achat (unique / abonnement), mise à jour du prix affiché sur le bouton.
 *
 * L'ajout au panier n'est PAS géré ici : le bloc rend un <product-form-component>
 * natif Horizon, ce composant se contente d'alimenter ses champs cachés
 * (id, quantity, selling_plan). Le flux natif s'occupe du fetch, des sections,
 * de l'événement cart et de l'ouverture du tiroir.
 */
class BundleProtocole extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bpReady === 'true') return;
    this.dataset.bpReady = 'true';

    this.offers = Array.from(this.querySelectorAll('[data-bp-offer]'));
    this.modes = Array.from(this.querySelectorAll('[data-bp-mode]'));
    this.variantInput = this.querySelector('input[name="id"]');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    this.cta = this.querySelector('[data-bp-cta]');
    this.ctaPrice = this.querySelector('[data-bp-cta-price]');

    if (!this.offers.length || !this.variantInput) return;

    this.mode = this.modes.find((mode) => mode.getAttribute('aria-checked') === 'true')?.dataset.bpMode || 'once';
    this.dataset.mode = this.mode;

    for (const offer of this.offers) {
      offer.addEventListener('click', () => this.selectOffer(offer));
      offer.addEventListener('keydown', (event) => this.#handleKeydown(event));
    }

    for (const mode of this.modes) {
      mode.addEventListener('click', () => this.selectMode(mode.dataset.bpMode));
    }

    // « Commencer mon cycle » ouvre toujours le panier, même si l'ouverture
    // automatique du tiroir est désactivée dans les réglages du thème.
    this.addEventListener('shopify:cart:lines-update', (event) => this.#openCartAfterAdd(event));

    const preselected = this.offers.find((offer) => offer.getAttribute('aria-checked') === 'true') || this.offers[0];
    this.selectOffer(preselected);
  }

  /** @param {HTMLElement} target */
  selectOffer(target) {
    // Un palier sans abonnement ne peut pas être acheté en mode abonnement :
    // on repasse en achat unique plutôt que d'afficher « Abonnement » à tort.
    if (this.mode === 'subscription' && !target.dataset.planId) {
      this.#setMode('once');
    }

    for (const offer of this.offers) {
      const selected = offer === target;
      offer.setAttribute('aria-checked', selected ? 'true' : 'false');
      offer.tabIndex = selected ? 0 : -1;
    }

    this.selected = target;
    this.#syncForm();
  }

  /** @param {string | undefined} mode */
  selectMode(mode) {
    if (!mode) return;
    this.#setMode(mode);

    // En abonnement, le palier choisi doit en proposer un : sinon on bascule
    // sur le premier palier qui en a un.
    if (mode === 'subscription' && !this.selected?.dataset.planId) {
      const withPlan = this.offers.find((offer) => offer.dataset.planId);
      if (withPlan) {
        this.selectOffer(withPlan);
        return;
      }
    }

    this.#syncForm();
  }

  /** @param {string} mode */
  #setMode(mode) {
    this.mode = mode;
    this.dataset.mode = mode;

    for (const button of this.modes) {
      button.setAttribute('aria-checked', button.dataset.bpMode === mode ? 'true' : 'false');
    }

    for (const offer of this.offers) this.#renderPrices(offer);
  }

  /**
   * Recopie le palier sélectionné dans les champs du formulaire produit natif.
   */
  #syncForm() {
    const offer = this.selected;
    if (!offer) return;

    this.#renderPrices(offer);

    if (this.variantInput) this.variantInput.value = offer.dataset.variantId || '';
    if (this.quantityInput) this.quantityInput.value = offer.dataset.quantity || '1';

    const planId = this.mode === 'subscription' ? offer.dataset.planId || '' : '';
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = planId;
      this.sellingPlanInput.disabled = planId === '';
    }

    const available = offer.dataset.available !== 'false';
    const button = this.cta?.tagName === 'BUTTON' ? this.cta : this.cta?.querySelector('button');
    if (button) button.disabled = !available;

    if (this.ctaPrice) {
      this.ctaPrice.textContent = this.#priceFor(offer, 'total');
    }
  }

  /**
   * Bascule les prix affichés d'un palier entre achat unique et abonnement.
   * @param {HTMLElement} offer
   */
  #renderPrices(offer) {
    const unit = offer.querySelector('[data-bp-unit]');
    const total = offer.querySelector('[data-bp-total]');

    if (unit) unit.textContent = this.#priceFor(offer, 'unit');
    if (total) total.textContent = this.#priceFor(offer, 'total');
  }

  /**
   * @param {HTMLElement} offer
   * @param {'unit' | 'total'} kind
   * @returns {string}
   */
  #priceFor(offer, kind) {
    const once = kind === 'unit' ? offer.dataset.unitOnce : offer.dataset.totalOnce;
    const sub = kind === 'unit' ? offer.dataset.unitSub : offer.dataset.totalSub;

    if (this.mode === 'subscription' && sub) return sub;
    return once || '';
  }

  /**
   * Ouvre le tiroir panier une fois l'ajout confirmé par le serveur.
   * @param {Event & {action?: string, promise?: Promise<any>}} event
   */
  #openCartAfterAdd(event) {
    if (event.action !== 'add' || !event.promise) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;

        const drawer = /** @type {HTMLElement & {open?: Function, isOpen?: boolean} | null} */ (
          document.getElementById('cart-drawer')
        );
        if (!drawer || typeof drawer.open !== 'function' || drawer.isOpen) return;

        // Laisse le tiroir recevoir le nouveau contenu avant de l'ouvrir.
        requestAnimationFrame(() => {
          if (!drawer.isOpen) drawer.open?.();
        });
      })
      .catch(() => {});
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    const current = this.offers.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = this.offers[(current + step + this.offers.length) % this.offers.length];

    this.selectOffer(next);
    next.focus();
  }
}

if (!customElements.get('bundle-protocole')) {
  customElements.define('bundle-protocole', BundleProtocole);
}
